import { CloseOutlined } from '@/app/icons';
import { Button, Select, Space, Typography } from 'antd';
import { departmentsOfBranch, groupByBranch } from '@/domain/branches';
import { changeViewFilter, isFiltering, selectableUsers } from '@/domain/view-filter';
import { useBranches, useDirectory } from '@/hooks/use-directory';
import { useCurrentUser } from '@/stores/session-store';
import { useViewFilterStore } from '@/stores/view-filter-store';
import { selectOptionMatches } from '@/domain/select-search';

/**
 * Bốn ô nối tầng chi nhánh → phòng → nhóm → người. Chỉ hiện với ai nhìn được người khác; ô chi nhánh
 * chỉ hiện khi Giám đốc đã khai từ hai chi nhánh trở lên (chốt 24/09/2026).
 * `stacked`: xếp dọc, mỗi ô rộng hết khung — cho bảng nổi trên màn hình hẹp (xem `AppLayout`).
 */
export function ViewFilterBar({ stacked = false }: { stacked?: boolean }) {
  const { permissions } = useCurrentUser();
  const directory = useDirectory();
  const branches = useBranches();
  const { filter, setFilter, reset } = useViewFilterStore();

  if (!permissions.seesOthers) return null;
  const size = stacked ? 'middle' : 'small';
  // Bề rộng CỐ ĐỊNH ở dạng nằm ngang: để ô tự nở theo tên đang chọn («Nguyễn Phan Phương Linh») thì
  // thanh trên cùng lúc một hàng, lúc hai hàng tuỳ người được chọn. Bảng chọn xổ xuống vẫn rộng tự do.
  const width = (pixels: number) => (stacked ? { width: '100%' } : { width: pixels });

  // Danh sách phòng/nhóm của cả công ty được gửi cho mọi người, nhưng chỉ đơn vị CÓ NGƯỜI trong
  // phạm vi nhìn mới đáng hiện — chọn một nhóm không thấy ai thì chỉ ra màn hình trống.
  const populated = (key: 'departmentId' | 'teamId', id: string) =>
    directory.users.some((user) => user[key] === id);
  const allDepartments = directory.departments.filter((d) => populated('departmentId', d.id));
  /** Chọn chi nhánh rồi thì ô phòng chỉ còn phòng của chi nhánh ấy. */
  const inBranch = filter.branchId ? departmentsOfBranch(branches, filter.branchId) : null;
  const departments = inBranch
    ? allDepartments.filter((department) => inBranch.has(department.id))
    : allDepartments;
  // Chi nhánh chưa có phòng nào CÓ NGƯỜI trong phạm vi thì chọn vào chỉ ra màn hình trống.
  const branchOptions = groupByBranch(branches, allDepartments)
    .map((group) => group.branch)
    .filter((branch) => branch !== null);
  const teams = directory
    .teamsOf(filter.departmentId || undefined)
    .filter((t) => populated('teamId', t.id));
  const people = selectableUsers(filter, directory.users, (id) =>
    departmentsOfBranch(branches, id),
  );
  const update = (change: Parameters<typeof changeViewFilter>[1]) =>
    setFilter(
      changeViewFilter(filter, change, {
        users: directory.users,
        teamDepartment: (teamId) =>
          directory.teamsOf().find((t) => t.id === teamId)?.parentId ?? null,
        departmentsOfBranch: (id) => departmentsOfBranch(branches, id),
      }),
    );

  return (
    <Space
      size={stacked ? 8 : 6}
      wrap={!stacked}
      orientation={stacked ? 'vertical' : 'horizontal'}
      style={stacked ? { width: 240 } : undefined}
    >
      {!stacked && <Typography.Text type="secondary">Xem</Typography.Text>}
      {branchOptions.length > 1 && (
        <Select
          size={size}
          style={width(150)}
          popupMatchSelectWidth={stacked}
          placeholder="Mọi chi nhánh"
          allowClear
          showSearch
          filterOption={selectOptionMatches}
          value={filter.branchId || undefined}
          onChange={(value?: string) => update({ branchId: value ?? '' })}
          options={branchOptions.map((branch) => ({ value: branch.id, label: branch.name }))}
        />
      )}
      {departments.length > 1 && (
        <Select
          size={size}
          style={width(140)}
          popupMatchSelectWidth={stacked}
          placeholder="Mọi phòng"
          allowClear
          showSearch
          filterOption={selectOptionMatches}
          value={filter.departmentId || undefined}
          onChange={(value?: string) => update({ departmentId: value ?? '' })}
          options={departments.map((d) => ({ value: d.id, label: d.name }))}
        />
      )}
      {teams.length > 1 && (
        <Select
          size={size}
          style={width(130)}
          popupMatchSelectWidth={stacked}
          placeholder="Mọi nhóm"
          allowClear
          showSearch
          filterOption={selectOptionMatches}
          value={filter.teamId || undefined}
          onChange={(value?: string) => update({ teamId: value ?? '' })}
          options={teams.map((t) => ({ value: t.id, label: t.name }))}
        />
      )}
      <Select
        size={size}
        style={width(160)}
        popupMatchSelectWidth={stacked}
        placeholder="Mọi người"
        allowClear
        showSearch
        filterOption={selectOptionMatches}
        value={filter.username || undefined}
        onChange={(value?: string) => update({ username: value ?? '' })}
        options={people.map((u) => ({ value: u.username, label: u.displayName }))}
      />
      {isFiltering(filter) && (
        <Button size={size} type="link" icon={<CloseOutlined />} onClick={reset}>
          Bỏ lọc
        </Button>
      )}
    </Space>
  );
}
